// plan-suggest.ts — Phase 2.3 Daily Planning Assistant (pure, no network).
// buildPlanRequest: candidate tasks + capacity + active goals → AiGenerateRequest.
// mapPlanResult: validate model picks against candidate ids, dedupe, cap at 3.

import type { AiGenerateRequest } from "./ai-client";
import type { LifeOsState } from "../../types";

export interface PlanCandidate {
  id: string;
  title: string;
  dueDate: string;
  priority: string;
  estimateMinutes: number;
  status: string;
  goal?: string;
}

export interface PlanPick {
  taskId: string;
  reason: string;
}

export const MAX_PICKS = 3;

/**
 * Gemini responseSchema. NOTE: KHÔNG dùng additionalProperties / minimum (Gemini 400).
 */
export const PLAN_RESPONSE_SCHEMA: Record<string, unknown> = {
  type: "object",
  required: ["picks"],
  properties: {
    picks: {
      type: "array",
      description: "Tối đa 3 việc quan trọng nhất, sắp theo thứ tự ưu tiên giảm dần.",
      items: {
        type: "object",
        required: ["taskId", "reason"],
        properties: {
          taskId: { type: "string", description: "id của việc (PHẢI nằm trong danh sách ứng viên)." },
          reason: { type: "string", description: "Một câu ngắn tiếng Việt: vì sao việc này quan trọng hôm nay." },
        },
      },
    },
  },
};

/** Build the candidate list from open (todo/doing) tasks, earliest-due first, capped. */
export function collectPlanCandidates(
  state: Pick<LifeOsState, "tasks" | "goals">,
  limit = 25,
): PlanCandidate[] {
  const goalTitle = (id?: string) => (id ? state.goals.find((g) => g.id === id)?.title : undefined);
  return state.tasks
    .filter((t) => t.status === "todo" || t.status === "doing")
    .slice()
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
    .slice(0, limit)
    .map((t) => {
      const goal = goalTitle(t.goalId);
      return {
        id: t.id,
        title: t.title,
        dueDate: t.dueDate,
        priority: t.priority,
        estimateMinutes: t.estimateMinutes,
        status: t.status,
        ...(goal ? { goal } : {}),
      };
    });
}

/** Build the AiGenerateRequest for MIT suggestion. PURE.
 *  `preference` = optional free-text personal criteria (D54) weighted above defaults. */
export function buildPlanRequest(
  candidates: PlanCandidate[],
  capacityMin: number,
  activeGoals: string[],
  today: string,
  preference = "",
): AiGenerateRequest {
  const lines = candidates
    .map(
      (c) =>
        `[${c.id}] "${c.title}" — hạn ${c.dueDate}, ưu tiên ${c.priority}, ~${c.estimateMinutes} phút` +
        `${c.goal ? `, mục tiêu: ${c.goal}` : ""}${c.status === "doing" ? ", đang làm dở" : ""}`,
    )
    .join("\n");
  const goalsLine = activeGoals.length ? activeGoals.join("; ") : "(không có)";
  const prefBlock = preference.trim()
    ? `\nƯU TIÊN CÁ NHÂN CỦA NGƯỜI DÙNG (đặt nặng nhất, ưu tiên hơn các nguyên tắc mặc định):\n"${preference.trim()}"\n`
    : "";

  const prompt = `Hôm nay là ${today}. Bạn là trợ lý lập kế hoạch ngày. Chọn 1–3 VIỆC QUAN TRỌNG NHẤT (MIT) cho hôm nay từ danh sách ứng viên, sắp theo thứ tự ưu tiên giảm dần.
${prefBlock}
Nguyên tắc mặc định:
- Ưu tiên việc QUÁ HẠN (hạn trước hôm nay) và việc ưu tiên cao.
- Ưu tiên việc gắn với mục tiêu đang theo đuổi.
- Tôn trọng SỨC CHỨA: tổng thời lượng các việc chọn nên ≤ ${capacityMin} phút.
- Tối đa 3 việc. CHỈ chọn taskId có trong danh sách dưới đây.

Mục tiêu đang theo: ${goalsLine}

Ứng viên:
${lines}

Trả về JSON đúng schema { picks: [{ taskId, reason }] }. "reason" là một câu ngắn vì sao chọn việc đó hôm nay.`;

  return {
    contents: [{ role: "user", parts: [{ text: prompt }] }],
    generationConfig: { maxOutputTokens: 400, temperature: 0.3 },
    responseSchema: PLAN_RESPONSE_SCHEMA,
  };
}

/** Validate picks: keep only ids in the candidate set, dedupe, cap at MAX_PICKS. Never throws. */
export function mapPlanResult(
  raw: { picks?: Array<{ taskId?: unknown; reason?: unknown }> } | null | undefined,
  candidateIds: Set<string>,
  max = MAX_PICKS,
): PlanPick[] {
  const picks = raw && Array.isArray(raw.picks) ? raw.picks : [];
  const seen = new Set<string>();
  const out: PlanPick[] = [];
  for (const p of picks) {
    const id = typeof p?.taskId === "string" ? p.taskId : "";
    if (!id || !candidateIds.has(id) || seen.has(id)) continue;
    seen.add(id);
    out.push({ taskId: id, reason: typeof p?.reason === "string" ? p.reason.trim() : "" });
    if (out.length >= max) break;
  }
  return out;
}
